import React, { useCallback, useEffect, useState } from "react";
import {
  searchInteractions,
  expandConversationUser,
} from "../utilities/interactionsHelpers";
import "../App.css";
import { DataTable, DxItemGroup, DxTextbox, LoadingPlaceholder } from "genesys-react-components";
import { GenesysDevIcons } from "genesys-dev-icons";

const ScreenRecordings = ({ pageCount, setPageCount, pageNumber, setPageNumber }) => {
  const [conversations, setConversations] = useState([]);
  const [interval, setInterval] = useState({
    "from": "",
    "to": ""
  })
  const [timezone, setTimezone] = useState("America/Vancouver");
  const [loading, setLoading] = useState(false);
  const [showNameList, setShowNameList] = useState(false);
  const [uniqueNames, setUniqueNames] = useState([]);

  const memoizedExpandConversationUser = useCallback(
    (userId) => expandConversationUser(userId, conversations),
    [conversations]
  );

  useEffect(() => {
    if (interval.from) {
      console.log('From date changed:', interval.from);
    }
  }, [interval.from]);

  useEffect(() => {
    if (interval.to) {
      console.log('To date changed:', interval.to);
    }
  }, [interval.to]);

  useEffect(() => {
    if (pageNumber >= 1 && interval.from && interval.to) {
      setLoading(true);

      // Use the same conversion function
      const convertToUTC = (dateTimeStr, tz) => {
        const [datePart, timePart] = dateTimeStr.split('T');
        const [year, month, day] = datePart.split('-');
        const [hour, minute] = timePart.split(':');

        const utcDateStr = `${year}-${month}-${day}T${hour}:${minute}:00.000Z`;
        const utcDate = new Date(utcDateStr);

        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: tz,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZoneName: 'short'
        });

        const parts = formatter.formatToParts(utcDate);
        const getPartValue = (type) => parts.find(p => p.type === type)?.value;

        const tzYear = parseInt(getPartValue('year'));
        const tzMonth = parseInt(getPartValue('month'));
        const tzDay = parseInt(getPartValue('day'));
        const tzHour = parseInt(getPartValue('hour'));
        const tzMinute = parseInt(getPartValue('minute'));

        const inputHour = parseInt(hour);
        const inputMinute = parseInt(minute);
        const inputYear = parseInt(year);
        const inputMonth = parseInt(month);
        const inputDay = parseInt(day);

        const inputDate = new Date(inputYear, inputMonth - 1, inputDay);
        const tzDate = new Date(tzYear, tzMonth - 1, tzDay);
        const dayDifference = Math.floor((inputDate - tzDate) / (1000 * 60 * 60 * 24));

        const inputTotalMinutes = dayDifference * 24 * 60 + inputHour * 60 + inputMinute;
        const tzTotalMinutes = tzHour * 60 + tzMinute;

        const offsetMinutes = inputTotalMinutes - tzTotalMinutes;

        const correctedUTC = new Date(utcDate.getTime() + offsetMinutes * 60000);

        return correctedUTC.toISOString();
      };

      const fromUTC = convertToUTC(interval.from, timezone);
      const toUTC = convertToUTC(interval.to, timezone);

      searchInteractions(`${fromUTC}/${toUTC}`, pageNumber).then((data) => {
        setConversations(data.conversations);

        // Fetch user details for the new page
        if (data.conversations && data.conversations.length > 0) {
          const uniqueUserIds = new Set();
          data.conversations.forEach((conversation) => {
            conversation.participants.forEach((participant) => {
              if (participant.purpose === "agent" && !participant.screenRecording) {
                uniqueUserIds.add(participant.userId);
              }
            });
          });

          Promise.all(
            Array.from(uniqueUserIds).map((userId) => expandConversationUser(userId, data.conversations))
          ).then(() => {
            setLoading(false);
            setConversations([...data.conversations]);
          });
        } else {
          setLoading(false);
        }
      });
    }
  }, [pageNumber]);

  useEffect(() => {
    if (conversations && conversations.length > 0) {
      setLoading(true);

      // Get unique user IDs from all conversations
      const uniqueUserIds = new Set();
      conversations.forEach((conversation) => {
        conversation.participants.forEach((participant) => {
          if (participant.purpose === "agent" && !participant.screenRecording) {
            uniqueUserIds.add(participant.userId);
          }
        });
      });

      // Fetch user details for each unique user ID
      Promise.all(
        Array.from(uniqueUserIds).map((userId) => memoizedExpandConversationUser(userId))
      ).then(() => {
        setLoading(false);
        // Force re-render with updated conversation data
        setConversations([...conversations]);
      });
    }
  }, [conversations?.length]);

  const conversationProps = {
    className: "table",
    headerRow: {
      cells: [
        {
          content: "Conversation Start",
          align: "left",
        },
        {
          content: "Conversation ID",
          align: "left",
        },
        {
          content: "User ID",
          align: "left",
        },
        {
          content: "Name",
          align: "left",
        },
        {
          content: "Email",
          align: "left",
        }
      ],
    },
    indentation: 0,
    rows: (conversations || []).map((conversation) => {
      const agent = conversation.participants?.find(p => p.purpose === "agent" && !p.screenRecording);
      const conversationStartPST = new Date(conversation.conversationStart).toLocaleString('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      return {
        cells: [
          {
            content: conversationStartPST,
            align: "center",
          },
          {
            content: conversation.conversationId,
            align: "left",
          },
          {
            content: agent?.userId || "N/A",
            align: "left",
          },
          {
            content: agent?.userDetails?.name || "",
            align: "left",
          },
          {
            content: agent?.userDetails?.email || "",
            align: "left",
          },
        ],
      };
    }),
  };

  const namesProps = {
    className: "table unique-names-table",
    headerRow: {
      cells: [
        {
          content: "Count",
          align: "left",
        },
        {
          content: "Unique Names",
          align: "left",
        },
        
      ],
    },
    indentation: 0,
    rows: (uniqueNames || []).map((name, index) => {
      return {
        cells: [
          {
            content: index + 1,
            align: "center",
          },
          {
            content: name,
            align: "left",
          }
        ],
      };
    })
  };

  const handleGenerateList = (e) => {
    e.preventDefault();

    // Extract unique names from conversations
    const namesSet = new Set();
    conversations.forEach((conversation) => {
      const agent = conversation.participants?.find(p => p.purpose === "agent" && !p.screenRecording);
      if (agent?.userDetails?.name) {
        namesSet.add(agent.userDetails.name);
      }
    });

    const namesList = Array.from(namesSet).sort();
    setUniqueNames(namesList);
    setShowNameList(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowNameList(false);

    // datetime-local gives us a string like "2025-12-15T10:00"
    // We want to treat this as being in the selected timezone, not browser local time
    // Then convert to UTC for the API

    const convertToUTC = (dateTimeStr, tz) => {
      // Parse the datetime string
      const [datePart, timePart] = dateTimeStr.split('T');
      const [year, month, day] = datePart.split('-');
      const [hour, minute] = timePart.split(':');

      // Create a date string that will be interpreted as UTC
      // Format: YYYY-MM-DDTHH:MM:SS.000Z
      const utcDateStr = `${year}-${month}-${day}T${hour}:${minute}:00.000Z`;
      const utcDate = new Date(utcDateStr);

      // Now get what this UTC time looks like in the target timezone
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZoneName: 'short'
      });

      const parts = formatter.formatToParts(utcDate);
      const getPartValue = (type) => parts.find(p => p.type === type)?.value;

      const tzYear = parseInt(getPartValue('year'));
      const tzMonth = parseInt(getPartValue('month'));
      const tzDay = parseInt(getPartValue('day'));
      const tzHour = parseInt(getPartValue('hour'));
      const tzMinute = parseInt(getPartValue('minute'));

      // Calculate the difference between what we want (input) and what we got (timezone)
      const inputHour = parseInt(hour);
      const inputMinute = parseInt(minute);
      const inputYear = parseInt(year);
      const inputMonth = parseInt(month);
      const inputDay = parseInt(day);

      // Calculate total minutes including date difference
      const inputDate = new Date(inputYear, inputMonth - 1, inputDay);
      const tzDate = new Date(tzYear, tzMonth - 1, tzDay);
      const dayDifference = Math.floor((inputDate - tzDate) / (1000 * 60 * 60 * 24));

      const inputTotalMinutes = dayDifference * 24 * 60 + inputHour * 60 + inputMinute;
      const tzTotalMinutes = tzHour * 60 + tzMinute;

      const offsetMinutes = inputTotalMinutes - tzTotalMinutes;

      // Adjust the UTC date by the offset
      const correctedUTC = new Date(utcDate.getTime() + offsetMinutes * 60000);

      return correctedUTC.toISOString();
    };

    const fromUTC = convertToUTC(interval.from, timezone);
    const toUTC = convertToUTC(interval.to, timezone);

    console.log(`Input times: ${interval.from} to ${interval.to}`);
    console.log(`Timezone: ${timezone}`);
    console.log(`Searching with interval: ${fromUTC}/${toUTC}`);
    setLoading(true);
    searchInteractions(`${fromUTC}/${toUTC}`, 1).then((data) => {
      setConversations(data.conversations);
      const totalPages = data.totalHits / 100;
      const decimal = totalPages % 1;
      setPageCount(decimal > 0.1 ? Math.floor(totalPages) + 1 : totalPages);

      // Fetch user details for the initial page
      if (data.conversations && data.conversations.length > 0) {
        const uniqueUserIds = new Set();
        data.conversations.forEach((conversation) => {
          conversation.participants.forEach((participant) => {
            if (participant.purpose === "agent" && !participant.screenRecording) {
              uniqueUserIds.add(participant.userId);
            }
          });
        });

        Promise.all(
          Array.from(uniqueUserIds).map((userId) => expandConversationUser(userId, data.conversations))
        ).then(() => {
          setLoading(false);
          setConversations([...data.conversations]);
          setPageNumber(1);
        });
      } else {
        setLoading(false);
        setPageNumber(1);
      }
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="screen-recording-form">
        <DxTextbox inputType="datetime-local" label="From" className="date-time-input" icon={GenesysDevIcons.IaAuthorization} clearButton={true}
          onChange={(value) => setInterval(prev => ({ ...prev, from: value }))}
        />
        <DxTextbox inputType="datetime-local" label="To" className="date-time-input" icon={GenesysDevIcons.IaAuthorization} clearButton={true}
          onChange={(value) => setInterval(prev => ({ ...prev, to: value }))}
        />
        <DxItemGroup
          className="date-time-dropdown"
          title="Timezone:"
          items={[
            { label: "Pacific (Vancouver)", value: "America/Vancouver" },
            { label: "Mountain (Edmonton)", value: "America/Edmonton" },
            { label: "Eastern (Toronto)", value: "America/Toronto" },
            { label: "UTC", value: "UTC" }
          ]}
          format="dropdown"
          onItemChanged={(item) => setTimezone(item.value)}
          selectedItems={[{ text: timezone.includes("Vancouver") ? "Pacific (Vancouver)" : timezone.includes("Edmonton") ? "Mountain (Edmonton)" : timezone.includes("Toronto") ? "Eastern (Toronto)" : "UTC", value: timezone }]}
        />
        <button type="submit" className="button-submit">
          Search
        </button>
        
      </form>

      {!loading && conversations && conversations.length > 0 && (
          <button type="button" onClick={handleGenerateList} className="button-generate-list">
            Generate List
          </button>
        )}
      {loading && <LoadingPlaceholder text="Fetching data" />}

      {!loading && (!conversations || conversations.length === 0) && <p>No conversations found</p>}

      {showNameList && uniqueNames.length > 0 && (
        <DataTable
            className="unique-names-table"
            headerRow={namesProps.headerRow}
            rows={namesProps.rows}
          />
        // <div style={{ marginTop: '20px', marginBottom: '20px' }}>
        //   <h3>Unique Names ({uniqueNames.length})</h3>
        //   <ul style={{ listStyle: 'none', padding: 0 }}>
        //     {uniqueNames.map((name, index) => (
        //       <li key={index} style={{ padding: '5px 0' }}>{name}</li>
        //     ))}
        //   </ul>
        // </div>
      )}

      {!loading && conversations && conversations.length > 0 && (
        <>
          <DataTable
            headerRow={conversationProps.headerRow}
            rows={conversationProps.rows}
          />
        </>
      )}
    </>
  );
};

export default ScreenRecordings;
